import {NCRNotesDataGridCellKeys} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import React, {useMemo, useRef} from "react";
import {NotebookTagKeys} from "../../../../../../../../core/models/static/routes/notebook-tags";
import {OpenInNew} from "@mui/icons-material";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {routeFunctions} from "../../../../../../../../core/models/static/routes/app-routes";
import {useHistory} from "react-router-dom";


const dataGridSaveConfig = {
    name: "NCRNotesDataGrid",
    version: 1,
};

/**
 * NCR notes list action names
 */
export const NCRListNotesNames = {
    Edit: 'edit',
    Delete: 'delete'
}

const NoteListView = ({notes, updateNote, removeNote, searchData, exportInfo}) => {
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridControlleRef = useRef(null);
    const history = useHistory();

    /**
     * Memo version of column in the data grid
     * @type {DataGridColumn[]}
     */
    const notesListColumns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 100,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: true,
                pinned: true,
            },
            {
                title: "Operator",
                name: NCRNotesDataGridCellKeys.submittedByName,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
            {
                title: "Submission Date",
                name: NCRNotesDataGridCellKeys.submitDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
            {
                title: "NCR Code",
                name: NCRNotesDataGridCellKeys.ncrCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 200,
            },
            {
                name: NCRNotesDataGridCellKeys.navigate,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                pinned: true,
                width: 0,
            },
            {
                title: "Title",
                name: NCRNotesDataGridCellKeys.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 200,
            },
            {
                title: "Note",
                name: NCRNotesDataGridCellKeys.content,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 200,
            },
        ], []);


    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: notesListColumns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: NCRListNotesNames.Edit,
                title: 'Edit',
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
            {
                name: NCRListNotesNames.Delete,
                title: 'Delete',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])


    /**
     * Opens the confirmation dialog regarding this note
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = (itemsToBeRemoved, allRecordsSelected) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected note${itemsToBeRemoved?.length > 1 || allRecordsSelected ? 's' : ''}?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeNote(itemsToBeRemoved),
        );
    }


    /**
     * Handles the action over the selected row in the table based on the name of the note
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        dataGridControlleRef.current?.resetSelection();
        switch (action?.name) {
            case NCRListNotesNames.Edit: {
                if (allRowsSelected && notes.length === 1) {
                    updateNote(notes[0]);
                } else {
                    updateNote(notes.find(e => e.id === (selectedRows[0].data)));
                }
                break;
            }
            case NCRListNotesNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows?.map(e => e.data));
                }
                return getRemoveConfirmation(notes?.map(e => e.data));
            }
            default:
                break;
        }
    }

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return notes?.map(note => {
            const ncrId = note?.tags?.find(e => e.key === NotebookTagKeys.query)?.value
            const ncrCode = note?.tags?.find(e => e.key === NotebookTagKeys.ncrCode)?.value
            return ({
                key: note.id,
                data: note.id,
                cells: {
                    [DataGridExoticColumnFields.selection]: null,
                    [NCRNotesDataGridCellKeys.navigate]:
                        ncrId &&
                        <IconButton
                            onPressed={() => history.push(routeFunctions.ncr.single(ncrId))}
                            color={'paper.secondary'}
                        >
                            <OpenInNew className='icon normal'/>
                        </IconButton>,
                    [NCRNotesDataGridCellKeys.title]: note?.title,
                    [NCRNotesDataGridCellKeys.submittedByName]: note?.createdByName,
                    [NCRNotesDataGridCellKeys.ncrCode]: ncrCode,
                    [NCRNotesDataGridCellKeys.submitDate]: note?.modifiedDateTime,
                    [NCRNotesDataGridCellKeys.content]:
                        <div
                            className={'note-content'}
                            dangerouslySetInnerHTML={{__html: note?.body ?? ""}}
                        />,
                },
            })
        })
    }, [notes, history]);


    return (
        <>

            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: notesListColumns,
                }}
                ref={dataGridControlleRef}
                columns={dataGridColumns}
                rows={dataGridRows}
                footerActions={footerActions}
                hideFooterActions={notes?.length < 1}
                onActionInvoked={onActionInvoked}
                hideSelectAll
                state={{
                    sortBy: searchData?.orderBy,
                    pagination: searchData?.paginationInfo,
                    loading: {state: searchData?.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={exportInfo}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    )
};


export default NoteListView;
